import './styles.scss';

import { DispatchIssueWithDocuments, IssueHistoryFormDefinitions } from '@/features/IssueHistory/components/IssueHistoryForm/types';
import { useEffect, useMemo } from 'react';
import useSession, { SessionUser } from '@/state/session';

import IssueHistoryForm from '@/features/IssueHistory/components/IssueHistoryForm';
import Popup from '@/common/Popup';
import { PopupTypes } from '@/common/Popup/types';
import { createComponentState } from '@/state';
import { getClasses } from '@/utils';
import useConfirmation from '@/hooks/useConfirmation';
import { useNavigate } from 'react-router-dom';

export type IssueHistoryDrawerState = {
  show: boolean;
  data: Partial<DispatchIssueWithDocuments>;
};
export const initIssueHistoryState: IssueHistoryDrawerState = {
  show: false,
  data: {},
};
export const useIssueHistoryDrawer = createComponentState<IssueHistoryDrawerState>(initIssueHistoryState);
export type IssueHistoryDrawerProps = { locale?: Record<string, string> } & PopupTypes.Props;

const IssueHistoryDrawer = ({ locale: customLocale, ...drawerProps }: IssueHistoryDrawerProps): JSX.Element => {
  const [state, setState] = useIssueHistoryDrawer(({ state, setState }) => [state, setState]);
  const { show, data } = state;
  const user = useSession(({ state }) => state.user || ({} as SessionUser));
  const navigate = useNavigate();
  const confirmHide = useConfirmation({
    Body: {
      message: (
        <div className="IssueConfirmation">
          <h5>Are you sure?</h5>
          <span>Any unsaved changes will be lost.</span>
        </div>
      ),
    },
  });

  const onHide = async (redirect: boolean = false): Promise<void> => {
    try {
      if (redirect) {
        IssueHistoryFormDefinitions.clearForm();
        setState({ ...state, show: false });
      } else {
        const issueValue = IssueHistoryFormDefinitions.loadForm();
        const issuesHasValue = Object.keys(issueValue).length > 0;
        if (issuesHasValue) await confirmHide();
        setState({ ...state, show: false });
      }
    } catch (err) {
      console.error('Error hiding drawer:', err);
    }
  };
  const onExited = (): void => setState(initIssueHistoryState);

  const onSubmit = (data): void => {
    debug.log('issue-history,issue-history.drawer', 'Submit Issue', data);
    IssueHistoryFormDefinitions.clearForm();
    onHide();
  };

  const gotoHistory = (): void => {
    onHide(true);
    navigate('/issue-history');
  };

  const actions = useMemo(() => {
    const result = [];
    if (!location.pathname.startsWith('/issue-history'))
      result.push({
        key: 'history',
        label: 'See History',
        onClick: gotoHistory,
      });
    return result.length > 0 ? result : undefined;
  }, [location.pathname, gotoHistory]);

  const value = useMemo(() => {
    if (!show) return undefined;
    const isDispatcher = user?.role?.name === 'dispatch';
    const dispatcherId = isDispatcher && !data?.dispatcherId ? user?.id : data?.dispatcherId;
    const result = { ...data, dispatcherId };
    debug.log('issue-history,issue-history.drawer', 'Default Value', result);
    return result;
  }, [show, data, user]);
  useEffect((): void => {
    if (show) return;
    IssueHistoryFormDefinitions.clearForm();
  }, [show]);

  return (
    <Popup
      {...drawerProps}
      className={getClasses('IssueHistoryDrawer', drawerProps.className)}
      show={show}
      onHide={onHide}
      onExited={onExited}
      actions={actions}
    >
      <IssueHistoryForm onSubmit={onSubmit} value={value} key={data?.id} />
    </Popup>
  );
};

export default IssueHistoryDrawer;
